import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Container, Paper, Button, Stack, Chip,
    CircularProgress, IconButton, Divider, Grid, Dialog,
    DialogTitle, DialogContent, DialogActions, TextField, Alert,
    Accordion, AccordionSummary, AccordionDetails, MenuItem,
} from '@mui/material';
import ArrowBackIcon       from '@mui/icons-material/ArrowBack';
import LocationOnIcon      from '@mui/icons-material/LocationOn';
import DirectionsBikeIcon  from '@mui/icons-material/DirectionsBike';
import StraightenIcon      from '@mui/icons-material/Straighten';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import CancelIcon          from '@mui/icons-material/Cancel';
import SaveIcon            from '@mui/icons-material/Save';
import CloseIcon           from '@mui/icons-material/Close';
import DeleteIcon          from '@mui/icons-material/Delete';
import AddIcon             from '@mui/icons-material/Add';
import ExpandMoreIcon      from '@mui/icons-material/ExpandMore';
import CheckroomIcon       from '@mui/icons-material/Checkroom';
import GpsFixedIcon        from '@mui/icons-material/GpsFixed';
import LocalDrinkIcon      from '@mui/icons-material/LocalDrink';
import LuggageIcon         from '@mui/icons-material/Luggage';
import BuildIcon           from '@mui/icons-material/Build';
import PersonIcon          from '@mui/icons-material/Person';
import PaymentsIcon        from '@mui/icons-material/Payments';

import ReactMarkdown from 'react-markdown';
import MiniCalendar  from './MiniCalendar';
import axiosPublic from '../api/axiosPublic';
import axios       from '../api/axiosConfig';
import { useAuth }     from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { useTranslation } from 'react-i18next';

const TERRACOTTA = '#C4622D';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';
const STONE      = '#EDE8E1';
const WALNUT     = '#2C1F14';
const MIDNIGHT   = '#1A1A2E';

const DIFFICULTY_COLORS = {
    EASY:   { bg: '#E8F5E9', color: '#2E7D32', border: '#A5D6A7' },
    MEDIUM: { bg: '#FFF3E0', color: '#E65100', border: '#FFCC80' },
    HARD:   { bg: '#FFEBEE', color: '#B71C1C', border: '#FFCDD2' },
};

const MATERIAL_META = [
    { key: 'bolsaManillar',      label: 'Handlebar bag',         Icon: CheckroomIcon },
    { key: 'gps',                label: 'GPS (downloadable)',     Icon: GpsFixedIcon },
    { key: 'soporteBotella',     label: 'Water bottle holder',   Icon: LocalDrinkIcon },
    { key: 'transportinAlforja', label: 'Pannier & rack',        Icon: LuggageIcon },
    { key: 'kitHerramientas',    label: 'Tool kit per room',     Icon: BuildIcon },
];

const DIFFICULTY_OPTIONS = ['EASY', 'MEDIUM', 'HARD'];

function StarRow({ score, size = '1.1rem' }) {
    return (
        <Stack direction="row" spacing={0.3} alignItems="center">
            {[1,2,3,4,5].map(n => (
                <Box key={n} component="span"
                     sx={{ color: n <= Math.round(score) ? GOLD : 'rgba(0,0,0,0.18)',
                         fontSize: size, lineHeight: 1 }}>★</Box>
            ))}
        </Stack>
    );
}

function IncludedList({ text, included }) {
    if (!text) return null;
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    return (
        <Stack spacing={0.8}>
            {lines.map((line, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                    {included
                        ? <CheckCircleIcon sx={{ color: FOREST, fontSize: '1.1rem', mt: '2px', flexShrink: 0 }} />
                        : <CancelIcon     sx={{ color: '#c62828', fontSize: '1.1rem', mt: '2px', flexShrink: 0 }} />}
                    <Typography variant="body2" sx={{ color: WALNUT, lineHeight: 1.6 }}>{line}</Typography>
                </Stack>
            ))}
        </Stack>
    );
}

export default function BikeTravelOfferDetails() {
    const { id }   = useParams();
    const navigate = useNavigate();
    const { t }    = useTranslation();
    const { user } = useAuth();
    const { format: formatMoney } = useCurrency();
    const fileInputRef = useRef(null);

    const isManager = user?.role === 'MANAGER' || user?.role === 'ADMIN';

    const [offer,          setOffer]          = useState(null);
    const [loading,        setLoading]        = useState(true);
    const [error,          setError]          = useState('');
    const [ratingSummary,  setRatingSummary]  = useState(null);
    const [reviews,        setReviews]        = useState([]);
    const [faqs,           setFaqs]           = useState([]);

    // Manager editing
    const [activeEdit,     setActiveEdit]     = useState(null); // field name
    const [tempValue,      setTempValue]      = useState('');
    const [imageFile,      setImageFile]      = useState(null);
    const [imagePreview,   setImagePreview]   = useState(null);
    const [editStop,         setEditStop]         = useState({ index: null, data: null });
    const [stopImageFile,    setStopImageFile]    = useState(null);
    const [stopImagePreview, setStopImagePreview] = useState(null);
    const [editFaq,          setEditFaq]          = useState({ index: null, data: null });
    const [statusLoading,  setStatusLoading]  = useState(false);
    const [deleteOpen,     setDeleteOpen]     = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteLoading,  setDeleteLoading]  = useState(false);
    const [deleteError,    setDeleteError]    = useState('');

    // Auth gate / booking dialog
    const [loginOpen, setLoginOpen] = useState(false);

    // Booking state
    const [bookingStep,    setBookingStep]    = useState(1); // 1 = date+mode, 2 = confirm
    const [bookingDate,    setBookingDate]    = useState(null);
    const [bookingPax,     setBookingPax]     = useState(1);
    const [bookingMode,    setBookingMode]    = useState(null); // 'BOOK_NOW' | 'RESERVE'
    const [bookingLoading, setBookingLoading] = useState(false);
    const [bookingError,   setBookingError]   = useState('');

    const minDateObj = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() + 15);
        return d;
    }, []);

    const totalCents   = (Number(offer?.basePrice) || 0) * bookingPax;
    const depositCents = Math.round(totalCents * 0.30);

    const handleOpenBooking = () => {
        setBookingStep(1);
        setBookingDate(null);
        setBookingPax(1);
        setBookingMode(null);
        setBookingError('');
        setLoginOpen(true);
    };

    const handleSelectMode = (mode) => {
        if (!bookingDate) { setBookingError('Please select a start date first.'); return; }
        if (bookingPax < 1) { setBookingError('At least 1 participant is required.'); return; }
        setBookingError('');
        setBookingMode(mode);
        setBookingStep(2);
    };

    const handleConfirmBooking = async () => {
        setBookingLoading(true);
        setBookingError('');
        try {
            const res = await axios.post(`/api/bike-travel-offers/book/${id}`, {
                startDate:        bookingDate.toISOString().split('T')[0],
                paymentType:      bookingMode,
                participantCount: bookingPax,
            });
            if (bookingMode === 'BOOK_NOW') {
                // DRAFT bill created — go to BookingDetails to pay
                window.location.href = `/booking/${res.data.billId}`;
            } else {
                // RESERVE — redirect to Stripe for 30% deposit
                window.location.href = res.data.sessionUrl;
            }
        } catch (err) {
            setBookingError(err.response?.data?.message || 'Could not create booking.');
            setBookingLoading(false);
        }
    };

    const fetchOffer = useCallback(async () => {
        try {
            const r = await axiosPublic.get(`/api/bike-travel-offers/view/${id}`);
            setOffer(r.data);
            setLoading(false);
            axiosPublic.get(`/api/ratings/summary?targetId=${id}&targetType=TRAVEL`)
                .then(res => setRatingSummary(res.data)).catch(() => {});
            axiosPublic.get(`/api/ratings/target?targetId=${id}&targetType=TRAVEL`)
                .then(res => setReviews(res.data || [])).catch(() => {});
            axiosPublic.get(`/api/faqs/view/offer/${id}`)
                .then(res => setFaqs(res.data || [])).catch(() => {});
        } catch {
            setError('This cycling offer could not be found.');
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { fetchOffer(); }, [fetchOffer]);

    // ── Manager helpers ──────────────────────────────────────────────────────
    const saveField = async (field, value) => {
        try {
            const updated = { ...offer, [field]: value };
            const fd = new FormData();
            fd.append('offer', new Blob([JSON.stringify(updated)], { type: 'application/json' }));
            await axios.put(`/api/bike-travel-offers/edit/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setOffer(updated);
            setActiveEdit(null);
        } catch { alert('Update failed.'); }
    };

    const saveImage = async () => {
        try {
            const fd = new FormData();
            fd.append('offer', new Blob([JSON.stringify(offer)], { type: 'application/json' }));
            fd.append('image', imageFile);
            await axios.put(`/api/bike-travel-offers/edit/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setImageFile(null); setImagePreview(null); fetchOffer();
        } catch { alert('Image save failed.'); }
    };

    const saveStop = async () => {
        try {
            const payload = { ...editStop.data, offerId: id, type: 'TRAVEL',
                stopOrder: editStop.index === -1 ? (offer.stops?.length || 0) : editStop.index };
            const fd = new FormData();
            fd.append('stop', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
            if (stopImageFile) fd.append('image', stopImageFile);
            const opts = { headers: { 'Content-Type': 'multipart/form-data' } };
            if (payload.id) await axios.put(`/stops/edit/${payload.id}`, fd, opts);
            else            await axios.post('/stops/stop', fd, opts);
            setEditStop({ index: null, data: null });
            setStopImageFile(null);
            setStopImagePreview(null);
            fetchOffer();
        } catch { alert('Failed to save stop.'); }
    };

    const deleteStop = async (stopId) => {
        try { await axios.delete(`/stops/delete/${stopId}`); setEditStop({ index: null, data: null }); fetchOffer(); }
        catch { alert('Failed to delete stop.'); }
    };

    const saveFaq = async () => {
        try {
            const payload = { ...editFaq.data, offerId: id, offerType: 'TRAVEL' };
            if (payload.id) await axios.put(`/api/faqs/edit/${payload.id}`, payload);
            else            await axios.post(`/api/faqs`, payload);
            setEditFaq({ index: null, data: null }); fetchOffer();
        } catch { alert('Failed to save FAQ.'); }
    };

    const deleteFaq = async (faqId) => {
        try { await axios.delete(`/api/faqs/delete/${faqId}`); setEditFaq({ index: null, data: null }); fetchOffer(); }
        catch { alert('Failed to delete FAQ.'); }
    };

    const handleStatusChange = async (newStatus) => {
        setStatusLoading(true);
        try {
            const res = await axios.patch(`/api/bike-travel-offers/status/${id}?status=${newStatus}`);
            setOffer(res.data);
        } catch { alert('Failed to change status.'); }
        finally { setStatusLoading(false); }
    };

    const handleDelete = async () => {
        setDeleteLoading(true); setDeleteError('');
        try {
            await axiosPublic.post('/api/app_user/login', { email: user.email, password: deletePassword });
        } catch {
            setDeleteError('Incorrect password.'); setDeleteLoading(false); return;
        }
        try {
            await axios.delete(`/api/bike-travel-offers/delete/${id}`);
            navigate('/bike-travel-offers');
        } catch {
            setDeleteError('Delete failed.'); setDeleteLoading(false);
        }
    };

    const editableSx = isManager ? {
        cursor: 'pointer', borderRadius: 1, transition: '0.15s',
        '&:hover': { backgroundColor: 'rgba(196,98,45,0.08)', outline: `1px dashed ${TERRACOTTA}` },
        padding: '3px 6px', margin: '-3px -6px',
    } : {};

    const startEdit = (field, val) => { if (!isManager) return; setActiveEdit(field); setTempValue(String(val ?? '')); };

    if (loading) return <Box display="flex" justifyContent="center" py={12}><CircularProgress /></Box>;
    if (error)   return <Box py={8} textAlign="center"><Typography color="error">{error}</Typography></Box>;

    const coverImg     = imagePreview || offer.imageUrl || '';
    const diffStyle    = DIFFICULTY_COLORS[offer.difficulty] || {};
    const deposit30    = Math.round((Number(offer.basePrice) || 0) * 0.30);
    const remaining70  = (Number(offer.basePrice) || 0) - deposit30;
    const canChangeStatus = isManager && (user?.id === offer?.createdById || user?.role === 'ADMIN');

    return (
        <Box sx={{ minHeight: '100vh', background: `linear-gradient(rgba(247,243,238,0.84), rgba(247,243,238,0.84)), url("https://www.originalarmenia.com/wp-content/uploads/2020/04/armenian-wine-areni.jpg") center/cover fixed` }}>
            <Helmet>
                <title>{offer.title} — HayTour</title>
                <meta name="description" content={offer.description?.slice(0, 155) || offer.title} />
            </Helmet>

            {/* ── Hero cover image ─────────────────────────────────────────── */}
            <Box sx={{ position: 'relative', height: { xs: '45vh', md: '90vh' }, overflow: 'hidden',
                cursor: isManager ? 'pointer' : 'default',
                '&:hover .img-overlay': { opacity: isManager ? 1 : 0 },
            }}
                onClick={() => isManager && !imageFile && fileInputRef.current?.click()}>
                <Box sx={{ position: 'absolute', inset: 0,
                    backgroundImage: `url(${coverImg})`,
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    filter: 'brightness(0.72)', transform: 'scale(1.04)', transition: '8s ease' }} />
                <Box sx={{ position: 'absolute', inset: 0,
                    background: 'linear-gradient(to bottom, rgba(26,26,46,0.15) 0%, rgba(26,26,46,0.75) 100%)' }} />

                {/* Edit overlay hint for manager */}
                <Box className="img-overlay" sx={{ position: 'absolute', inset: 0,
                    bgcolor: 'rgba(196,98,45,0.4)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', opacity: 0, transition: '0.3s' }}>
                    <Typography variant="h6" color="white" fontWeight="bold">Click to change cover image</Typography>
                </Box>

                {/* Title block */}
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 3, md: 5 } }}>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" mb={1.5}>
                        {offer.difficulty && (
                            <Chip label={offer.difficulty}
                                  size="small"
                                  sx={{ bgcolor: diffStyle.bg, color: diffStyle.color,
                                      border: `1px solid ${diffStyle.border}`,
                                      fontWeight: 800, fontSize: '0.72rem' }} />
                        )}
                        {offer.distance && (
                            <Chip icon={<StraightenIcon sx={{ fontSize: '0.85rem !important', color: 'rgba(255,255,255,0.8) !important' }} />}
                                  label={`${offer.distance} km`}
                                  size="small"
                                  sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white',
                                      border: '1px solid rgba(255,255,255,0.3)', fontWeight: 700 }} />
                        )}
                        {offer.duration && (
                            <Chip
                                label={`${offer.duration} days`}
                                size="small"
                                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 700,
                                    border: '1px solid rgba(255,255,255,0.3)' }} />
                        )}
                        {offer.region && (
                            <Chip icon={<LocationOnIcon sx={{ fontSize: '0.85rem !important', color: 'rgba(255,255,255,0.8) !important' }} />}
                                  label={offer.region}
                                  size="small"
                                  sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white',
                                      border: '1px solid rgba(255,255,255,0.3)', fontWeight: 700 }} />
                        )}
                    </Stack>

                    <Typography variant="h3" fontWeight={900}
                                sx={{ color: 'white', fontFamily: "'Playfair Display', Georgia, serif",
                                    fontSize: { xs: '1.8rem', md: '2.8rem' },
                                    textShadow: '0 2px 20px rgba(0,0,0,0.5)', lineHeight: 1.2 }}>
                        {offer.title}
                    </Typography>

                    {ratingSummary && ratingSummary.ratingCount > 0 && (
                        <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                            <StarRow score={ratingSummary.averageScore} />
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 700 }}>
                                {ratingSummary.averageScore?.toFixed(1)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                ({ratingSummary.ratingCount} review{ratingSummary.ratingCount !== 1 ? 's' : ''})
                            </Typography>
                        </Stack>
                    )}
                </Box>
            </Box>
            <input type="file" hidden ref={fileInputRef}
                   onChange={e => { const f = e.target.files[0]; if (f) { setImageFile(f); setImagePreview(URL.createObjectURL(f)); } }} />
            {imageFile && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, py: 1, bgcolor: '#fff', borderBottom: `1px solid ${STONE}` }}>
                    <Button variant="contained" size="small" onClick={saveImage} startIcon={<SaveIcon />}>Save image</Button>
                    <IconButton size="small" onClick={() => { setImageFile(null); setImagePreview(null); }}><CloseIcon /></IconButton>
                </Box>
            )}

            <Container maxWidth="xl" sx={{ py: 4 }}>
                {/* ── Top bar ── */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ color: TERRACOTTA, fontWeight: 'bold' }}>
                        Back
                    </Button>
                    {isManager && (
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip label={offer.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                                  color={offer.status === 'ACTIVE' ? 'success' : 'default'}
                                  size="small" sx={{ fontWeight: 700 }} />
                            {canChangeStatus && (
                                <Button variant="outlined" size="small" disabled={statusLoading}
                                        onClick={() => handleStatusChange(offer.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                                        sx={{ borderRadius: 2, fontWeight: 700,
                                            color: offer.status === 'ACTIVE' ? '#ed6c02' : '#2e7d32',
                                            borderColor: offer.status === 'ACTIVE' ? '#ed6c02' : '#2e7d32' }}>
                                    {statusLoading ? '…' : offer.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                                </Button>
                            )}
                            <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />}
                                    onClick={() => { setDeleteOpen(true); setDeletePassword(''); setDeleteError(''); }}
                                    sx={{ borderRadius: 2, fontWeight: 700 }}>
                                Delete
                            </Button>
                        </Stack>
                    )}
                </Box>

                {/* ── Two-column layout ── */}
                <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>

                    {/* ═══ LEFT COLUMN ═══ */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>

                        {/* Title — editable for managers */}
                        <Box mb={3}>
                            {activeEdit === 'title' ? (
                                <Box>
                                    <TextField fullWidth variant="standard" value={tempValue}
                                               onChange={e => setTempValue(e.target.value)}
                                               inputProps={{ style: { fontSize: '2rem', fontWeight: 900 } }} autoFocus />
                                    <Stack direction="row" spacing={1} mt={1}>
                                        <Button variant="contained" size="small" color="success"
                                                onClick={() => saveField('title', tempValue)}>Save</Button>
                                        <Button size="small" onClick={() => setActiveEdit(null)}>Cancel</Button>
                                    </Stack>
                                </Box>
                            ) : (
                                <Typography variant="h4" fontWeight={900}
                                            sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif",
                                                lineHeight: 1.2, ...editableSx }}
                                            onClick={() => startEdit('title', offer.title)}>
                                    {offer.title}
                                </Typography>
                            )}
                        </Box>

                        {/* Description */}
                        <Box mb={4}>
                            <Typography variant="h5" fontWeight={800}
                                        sx={{ color: TERRACOTTA, mb: 2, fontFamily: "'Playfair Display', Georgia, serif" }}>
                                About this journey
                            </Typography>
                            {activeEdit === 'description' ? (
                                <Box>
                                    <TextField multiline fullWidth rows={10} value={tempValue}
                                               onChange={e => setTempValue(e.target.value)} />
                                    <Stack direction="row" spacing={1} mt={1}>
                                        <Button variant="contained" size="small" color="success"
                                                onClick={() => saveField('description', tempValue)}>Save</Button>
                                        <Button size="small" onClick={() => setActiveEdit(null)}>Cancel</Button>
                                    </Stack>
                                </Box>
                            ) : (
                                <Box sx={{ ...editableSx }} onClick={() => startEdit('description', offer.description)}>
                                    {offer.description
                                        ? <ReactMarkdown components={{
                                            p:  (props) => <Typography variant="body1" paragraph sx={{ color: '#4A3728', lineHeight: 1.9 }} {...props} />,
                                            h1: (props) => <Typography variant="h5" fontWeight={700} sx={{ color: WALNUT, mt: 2, mb: 1 }} {...props} />,
                                            h2: (props) => <Typography variant="h6" fontWeight={700} sx={{ color: WALNUT, mt: 1.5, mb: 0.8 }} {...props} />,
                                            h3: (props) => <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT, mt: 1, mb: 0.5 }} {...props} />,
                                          }}>{offer.description}</ReactMarkdown>
                                        : isManager && <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Click to add description</Typography>}
                                </Box>
                            )}
                        </Box>

                        <Divider sx={{ mb: 4 }} />

                        {/* Included / Excluded */}
                        <Grid container spacing={3} mb={4}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="h6" fontWeight={800} sx={{ color: FOREST, mb: 2 }}>
                                    What's included
                                </Typography>
                                {activeEdit === 'included' ? (
                                    <Box>
                                        <TextField multiline fullWidth rows={6} value={tempValue}
                                                   onChange={e => setTempValue(e.target.value)}
                                                   placeholder="One item per line" />
                                        <Stack direction="row" spacing={1} mt={1}>
                                            <Button variant="contained" size="small" color="success"
                                                    onClick={() => saveField('included', tempValue)}>Save</Button>
                                            <Button size="small" onClick={() => setActiveEdit(null)}>Cancel</Button>
                                        </Stack>
                                    </Box>
                                ) : (
                                    <Box sx={{ ...editableSx }} onClick={() => startEdit('included', offer.included)}>
                                        <IncludedList text={offer.included} included />
                                        {!offer.included && isManager && (
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                                Click to add included items (one per line)
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="h6" fontWeight={800} sx={{ color: '#c62828', mb: 2 }}>
                                    Not included
                                </Typography>
                                {activeEdit === 'excluded' ? (
                                    <Box>
                                        <TextField multiline fullWidth rows={6} value={tempValue}
                                                   onChange={e => setTempValue(e.target.value)}
                                                   placeholder="One item per line" />
                                        <Stack direction="row" spacing={1} mt={1}>
                                            <Button variant="contained" size="small" color="success"
                                                    onClick={() => saveField('excluded', tempValue)}>Save</Button>
                                            <Button size="small" onClick={() => setActiveEdit(null)}>Cancel</Button>
                                        </Stack>
                                    </Box>
                                ) : (
                                    <Box sx={{ ...editableSx }} onClick={() => startEdit('excluded', offer.excluded)}>
                                        <IncludedList text={offer.excluded} included={false} />
                                        {!offer.excluded && isManager && (
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                                Click to add excluded items (one per line)
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Grid>
                        </Grid>

                        <Divider sx={{ mb: 4 }} />

                        {/* Materials */}
                        <Box mb={4}>
                            <Typography variant="h6" fontWeight={800} sx={{ color: WALNUT, mb: 2 }}>
                                Equipment provided
                            </Typography>
                            <Stack spacing={1.2}>
                                {MATERIAL_META.map(({ key, label, Icon }) => {
                                    const active = offer[key];
                                    return (
                                        <Stack key={key} direction="row" spacing={1.5} alignItems="center"
                                               onClick={isManager ? () => saveField(key, !active) : undefined}
                                               sx={{ pl: 1.5, py: 0.8,
                                                   borderLeft: `2px solid ${active ? FOREST : 'rgba(0,0,0,0.12)'}`,
                                                   opacity: active ? 1 : 0.45,
                                                   cursor: isManager ? 'pointer' : 'default',
                                                   '&:hover': isManager ? { bgcolor: 'rgba(196,98,45,0.04)', borderRadius: '0 4px 4px 0' } : {} }}>
                                            <Icon sx={{ color: active ? FOREST : 'text.disabled', fontSize: '1.2rem' }} />
                                            <Typography variant="body2" fontWeight={active ? 700 : 400}
                                                        sx={{ color: active ? WALNUT : 'text.secondary' }}>
                                                {label}
                                            </Typography>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Box>

                        <Divider sx={{ mb: 4 }} />

                        {/* Itinerary */}
                        {((offer.stops && offer.stops.length > 0) || isManager) && (
                            <Box mb={4}>
                                <Typography variant="h5" fontWeight={800} mb={3}
                                            sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                                    Itinerary
                                </Typography>

                                <Stack spacing={0}>
                                    {(offer.stops || []).map((stop, index) => (
                                        <Box key={stop.id || index}>
                                            {editStop.index === index ? (
                                                <Paper sx={{ p: 3, border: `1px solid ${TERRACOTTA}`, borderRadius: 2, mb: 3 }}>
                                                    <TextField size="small" fullWidth label="Stop title" variant="standard" sx={{ mb: 2 }}
                                                               value={editStop.data?.title || ''}
                                                               onChange={e => setEditStop({ ...editStop, data: { ...editStop.data, title: e.target.value } })} />
                                                    <TextField size="small" fullWidth label="Description" variant="outlined" multiline rows={4}
                                                               value={editStop.data?.description || ''}
                                                               onChange={e => setEditStop({ ...editStop, data: { ...editStop.data, description: e.target.value } })} />
                                                    <Box mt={2}>
                                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>Stop image</Typography>
                                                        {(stopImagePreview || editStop.data?.imageUrl) && (
                                                            <Box component="img"
                                                                 src={stopImagePreview || editStop.data.imageUrl}
                                                                 alt="preview"
                                                                 sx={{ width: '100%', maxHeight: 140, objectFit: 'cover', borderRadius: 1, mb: 1 }} />
                                                        )}
                                                        <Button variant="outlined" size="small" component="label"
                                                                sx={{ borderColor: TERRACOTTA, color: TERRACOTTA }}>
                                                            {stopImagePreview ? 'Change image' : 'Upload image'}
                                                            <input type="file" hidden accept="image/*"
                                                                   onChange={e => { const f = e.target.files[0]; if (f) { setStopImageFile(f); setStopImagePreview(URL.createObjectURL(f)); } }} />
                                                        </Button>
                                                        {stopImagePreview && (
                                                            <Button size="small" sx={{ ml: 1 }}
                                                                    onClick={() => { setStopImageFile(null); setStopImagePreview(null); }}>Remove</Button>
                                                        )}
                                                    </Box>
                                                    <Stack direction="row" spacing={1} mt={2}>
                                                        <Button size="small" variant="contained" onClick={saveStop}>Save</Button>
                                                        <Button size="small" onClick={() => { setEditStop({ index: null, data: null }); setStopImageFile(null); setStopImagePreview(null); }}>Cancel</Button>
                                                        <IconButton color="error" onClick={() => deleteStop(stop.id)} sx={{ ml: 'auto' }}><DeleteIcon /></IconButton>
                                                    </Stack>
                                                </Paper>
                                            ) : (
                                                <Box sx={{ py: 4, bgcolor: index % 2 === 0 ? 'transparent' : 'rgba(237,232,225,0.45)',
                                                    borderRadius: 2, px: 2 }}>
                                                    {/* Number badge + title row */}
                                                    <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                                        <Box sx={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%',
                                                            bgcolor: GOLD, display: 'flex', alignItems: 'center',
                                                            justifyContent: 'center',
                                                            boxShadow: '0 2px 8px rgba(201,151,58,0.4)' }}>
                                                            <Typography fontWeight={900} sx={{ color: MIDNIGHT, fontSize: '0.85rem' }}>
                                                                {index + 1}
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="h6" fontWeight="bold"
                                                                    sx={{ color: WALNUT, flex: 1, ...editableSx }}
                                                                    onClick={() => isManager && setEditStop({ index, data: stop })}>
                                                            {stop.title}
                                                        </Typography>
                                                        {isManager && (
                                                            <Button size="small" sx={{ color: TERRACOTTA, p: 0, textTransform: 'none', fontSize: '0.78rem', flexShrink: 0 }}
                                                                    onClick={() => setEditStop({ index, data: stop })}>
                                                                Edit stop
                                                            </Button>
                                                        )}
                                                    </Stack>
                                                    {/* Image floated right — description wraps around it */}
                                                    <Box sx={{ overflow: 'hidden' }}>
                                                        <Box sx={{ float: { xs: 'none', md: 'right' },
                                                            width: { xs: '100%', md: '52%' },
                                                            ml: { md: 3 }, mb: { xs: 2, md: 1 },
                                                            borderRadius: 2, overflow: 'hidden',
                                                            boxShadow: '0 4px 20px rgba(44,31,20,0.14)' }}>
                                                            <Box sx={{ paddingTop: '56.25%', position: 'relative' }}>
                                                                <Box component="img"
                                                                     src={stop.imageUrl || coverImg}
                                                                     alt={stop.title}
                                                                     sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%',
                                                                         objectFit: 'cover', objectPosition: 'center',
                                                                         filter: `brightness(${0.82 + (index % 3) * 0.06})` }} />
                                                                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0,
                                                                    background: 'linear-gradient(to top, rgba(26,26,46,0.75) 0%, transparent 100%)',
                                                                    px: 2, py: 1.5 }}>
                                                                    <Typography variant="caption"
                                                                                sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '0.75rem' }}>
                                                                        Day {index + 1} · {stop.title}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                        <Box sx={{ ...editableSx }} onClick={() => isManager && setEditStop({ index, data: stop })}>
                                                            {stop.description
                                                                ? <ReactMarkdown components={{
                                                                    p:  (props) => <Typography variant="body1" color="textSecondary" paragraph sx={{ lineHeight: 1.8 }} {...props} />,
                                                                    h1: (props) => <Typography variant="h6" fontWeight={700} sx={{ color: WALNUT, mt: 1.5, mb: 0.8 }} {...props} />,
                                                                    h2: (props) => <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT, mt: 1, mb: 0.5 }} {...props} />,
                                                                    h3: (props) => <Typography variant="subtitle2" fontWeight={700} sx={{ color: WALNUT, mt: 0.8, mb: 0.3 }} {...props} />,
                                                                  }}>{stop.description}</ReactMarkdown>
                                                                : isManager && <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Click to add description</Typography>}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    ))}

                                    {isManager && editStop.index === -1 && (
                                        <Paper sx={{ p: 3, border: '2px dashed #4A8C5C', borderRadius: 2, bgcolor: '#f6faf7', mt: 2 }}>
                                            <Typography variant="subtitle2" color="success.main" fontWeight="bold" mb={2}>New Stop</Typography>
                                            <TextField size="small" fullWidth label="Stop title" variant="standard" sx={{ mb: 2 }}
                                                       value={editStop.data?.title || ''}
                                                       onChange={e => setEditStop({ ...editStop, data: { ...editStop.data, title: e.target.value } })} />
                                            <TextField size="small" fullWidth label="Description" variant="outlined" multiline rows={3}
                                                       value={editStop.data?.description || ''}
                                                       onChange={e => setEditStop({ ...editStop, data: { ...editStop.data, description: e.target.value } })} />
                                            <Box mt={2}>
                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>Stop image</Typography>
                                                {stopImagePreview && (
                                                    <Box component="img" src={stopImagePreview} alt="preview"
                                                         sx={{ width: '100%', maxHeight: 140, objectFit: 'cover', borderRadius: 1, mb: 1 }} />
                                                )}
                                                <Button variant="outlined" size="small" component="label"
                                                        sx={{ borderColor: FOREST, color: FOREST }}>
                                                    {stopImagePreview ? 'Change image' : 'Upload image'}
                                                    <input type="file" hidden accept="image/*"
                                                           onChange={e => { const f = e.target.files[0]; if (f) { setStopImageFile(f); setStopImagePreview(URL.createObjectURL(f)); } }} />
                                                </Button>
                                                {stopImagePreview && (
                                                    <Button size="small" sx={{ ml: 1 }}
                                                            onClick={() => { setStopImageFile(null); setStopImagePreview(null); }}>Remove</Button>
                                                )}
                                            </Box>
                                            <Stack direction="row" spacing={1} mt={2}>
                                                <Button size="small" variant="contained" color="success" onClick={saveStop}>Create</Button>
                                                <Button size="small" onClick={() => { setEditStop({ index: null, data: null }); setStopImageFile(null); setStopImagePreview(null); }}>Cancel</Button>
                                            </Stack>
                                        </Paper>
                                    )}
                                    {isManager && editStop.index === null && (
                                        <Button startIcon={<AddIcon />} variant="outlined"
                                                sx={{ mt: 3, color: GOLD, borderColor: GOLD,
                                                    '&:hover': { borderColor: GOLD, bgcolor: 'rgba(201,151,58,0.06)' } }}
                                                onClick={() => setEditStop({ index: -1, data: { title: '', description: '' } })}>
                                            Add Stop
                                        </Button>
                                    )}
                                </Stack>
                            </Box>
                        )}

                        <Divider sx={{ mb: 4 }} />

                        {/* FAQ */}
                        {(faqs.length > 0 || isManager) && (
                            <Box mb={4}>
                                <Typography variant="h5" fontWeight={800} mb={3}
                                            sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                                    Frequently Asked Questions
                                </Typography>

                                {faqs.map((faq, index) => (
                                    <Box key={faq.id || index}>
                                        {editFaq.index === index ? (
                                            <Paper sx={{ p: 2.5, border: `1px solid ${TERRACOTTA}`, borderRadius: 2, mb: 1.5 }}>
                                                <TextField size="small" fullWidth label="Question" variant="standard" sx={{ mb: 1.5 }}
                                                           value={editFaq.data?.question || ''}
                                                           onChange={e => setEditFaq({ ...editFaq, data: { ...editFaq.data, question: e.target.value } })} />
                                                <TextField size="small" fullWidth label="Answer" multiline rows={3}
                                                           value={editFaq.data?.answer || ''}
                                                           onChange={e => setEditFaq({ ...editFaq, data: { ...editFaq.data, answer: e.target.value } })} />
                                                <Stack direction="row" spacing={1} mt={1.5}>
                                                    <Button size="small" variant="contained" onClick={saveFaq}>Save</Button>
                                                    <Button size="small" onClick={() => setEditFaq({ index: null, data: null })}>Cancel</Button>
                                                    <IconButton size="small" color="error" onClick={() => deleteFaq(faq.id)} sx={{ ml: 'auto' }}><DeleteIcon /></IconButton>
                                                </Stack>
                                            </Paper>
                                        ) : (
                                            <Accordion disableGutters elevation={0}
                                                       sx={{ mb: 1, border: `1px solid rgba(196,98,45,0.15)`,
                                                           borderRadius: '10px !important', bgcolor: '#fff',
                                                           '&:before': { display: 'none' } }}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: TERRACOTTA }} />}
                                                                  sx={{ borderRadius: '10px',
                                                                      '& .MuiAccordionSummary-content': { my: 1.5 } }}>
                                                    <Typography fontWeight={700} sx={{ color: WALNUT }}>{faq.question}</Typography>
                                                    {isManager && (
                                                        <IconButton size="small" sx={{ ml: 1 }}
                                                                    onClick={e => { e.stopPropagation(); setEditFaq({ index, data: faq }); }}>
                                                            <SaveIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                                                    <Typography variant="body2" sx={{ color: '#4A3728', lineHeight: 1.8 }}>
                                                        {faq.answer}
                                                    </Typography>
                                                </AccordionDetails>
                                            </Accordion>
                                        )}
                                    </Box>
                                ))}

                                {isManager && editFaq.index === -1 && (
                                    <Paper sx={{ p: 2.5, border: '2px dashed #4A8C5C', borderRadius: 2, bgcolor: '#f6faf7', mt: 1.5 }}>
                                        <Typography variant="subtitle2" color="success.main" fontWeight="bold" mb={1.5}>New FAQ</Typography>
                                        <TextField size="small" fullWidth label="Question" variant="standard" sx={{ mb: 1.5 }}
                                                   value={editFaq.data?.question || ''}
                                                   onChange={e => setEditFaq({ ...editFaq, data: { ...editFaq.data, question: e.target.value } })} />
                                        <TextField size="small" fullWidth label="Answer" multiline rows={3}
                                                   value={editFaq.data?.answer || ''}
                                                   onChange={e => setEditFaq({ ...editFaq, data: { ...editFaq.data, answer: e.target.value } })} />
                                        <Stack direction="row" spacing={1} mt={1.5}>
                                            <Button size="small" variant="contained" color="success" onClick={saveFaq}>Create</Button>
                                            <Button size="small" onClick={() => setEditFaq({ index: null, data: null })}>Cancel</Button>
                                        </Stack>
                                    </Paper>
                                )}
                                {isManager && editFaq.index === null && (
                                    <Button startIcon={<AddIcon />} variant="outlined" size="small"
                                            sx={{ mt: 1.5, color: TERRACOTTA, borderColor: TERRACOTTA }}
                                            onClick={() => setEditFaq({ index: -1, data: { question: '', answer: '' } })}>
                                        Add FAQ
                                    </Button>
                                )}
                            </Box>
                        )}

                        <Divider sx={{ mb: 4 }} />

                        {/* Reviews */}
                        {reviews.length > 0 && (
                            <Box mb={4}>
                                <Typography variant="h5" fontWeight={800} mb={3}
                                            sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                                    Customer Reviews
                                    {ratingSummary?.ratingCount > 0 && (
                                        <Typography component="span" variant="body1"
                                                    sx={{ ml: 1.5, color: 'text.secondary', fontWeight: 400, fontFamily: 'inherit' }}>
                                            ({ratingSummary.averageScore?.toFixed(1)} / 5 · {ratingSummary.ratingCount} review{ratingSummary.ratingCount !== 1 ? 's' : ''})
                                        </Typography>
                                    )}
                                </Typography>
                                <Stack spacing={2}>
                                    {reviews.map(review => (
                                        <Paper key={review.id} elevation={0}
                                               sx={{ p: 2.5, border: `1px solid ${STONE}`,
                                                   borderRadius: 3, bgcolor: '#fff' }}>
                                            <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                                                {review.reviewerImageUrl
                                                    ? <Box component="img" src={review.reviewerImageUrl} alt={review.reviewerName}
                                                           sx={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                                                    : <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: STONE,
                                                           display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                          <PersonIcon sx={{ color: TERRACOTTA, fontSize: '1.1rem' }} />
                                                      </Box>}
                                                <Box>
                                                    <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT }}>
                                                        {review.reviewerName || 'Anonymous'}
                                                    </Typography>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <StarRow score={review.score} size="0.85rem" />
                                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                            {review.createdAt ? new Date(review.createdAt).toLocaleDateString() : ''}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                            {review.comment && (
                                                <Typography variant="body2" sx={{ color: '#4A3728', lineHeight: 1.7, pl: 6 }}>
                                                    "{review.comment}"
                                                </Typography>
                                            )}
                                        </Paper>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Box>

                    {/* ═══ RIGHT COLUMN — sticky booking card ═══ */}
                    <Box sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0, position: { md: 'sticky' }, top: { md: 88 } }}>

                        {/* Manager field editors for offer-specific fields */}
                        {isManager && (
                            <Paper elevation={0} sx={{ p: 2, mb: 2, border: `1px solid rgba(196,98,45,0.18)`,
                                borderRadius: 2, bgcolor: '#fff' }}>
                                <Typography variant="caption" fontWeight={900} color="textSecondary"
                                            sx={{ letterSpacing: '0.1em', display: 'block', mb: 1.5 }}>
                                    MANAGE FIELDS
                                </Typography>
                                <Stack spacing={1}>
                                    {/* Region */}
                                    {activeEdit === 'region' ? (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <TextField size="small" label="Region" value={tempValue}
                                                       onChange={e => setTempValue(e.target.value)} sx={{ flex: 1 }} />
                                            <IconButton size="small" color="success" onClick={() => saveField('region', tempValue)}><SaveIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => setActiveEdit(null)}><CloseIcon fontSize="small" /></IconButton>
                                        </Stack>
                                    ) : (
                                        <Button size="small" variant="text" onClick={() => startEdit('region', offer.region)}
                                                sx={{ justifyContent: 'flex-start', color: WALNUT, textTransform: 'none' }}>
                                            Region: {offer.region || '—'}
                                        </Button>
                                    )}
                                    {/* Difficulty */}
                                    {activeEdit === 'difficulty' ? (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <TextField select size="small" label="Difficulty" value={tempValue}
                                                       onChange={e => setTempValue(e.target.value)} sx={{ flex: 1 }}>
                                                {DIFFICULTY_OPTIONS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                                            </TextField>
                                            <IconButton size="small" color="success" onClick={() => saveField('difficulty', tempValue)}><SaveIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => setActiveEdit(null)}><CloseIcon fontSize="small" /></IconButton>
                                        </Stack>
                                    ) : (
                                        <Button size="small" variant="text" onClick={() => startEdit('difficulty', offer.difficulty)}
                                                sx={{ justifyContent: 'flex-start', color: WALNUT, textTransform: 'none' }}>
                                            Difficulty: {offer.difficulty || '—'}
                                        </Button>
                                    )}
                                    {/* Distance */}
                                    {activeEdit === 'distance' ? (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <TextField size="small" type="number" label="Distance (km)" value={tempValue}
                                                       onChange={e => setTempValue(e.target.value)} sx={{ flex: 1 }} />
                                            <IconButton size="small" color="success" onClick={() => saveField('distance', parseFloat(tempValue) || 0)}><SaveIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => setActiveEdit(null)}><CloseIcon fontSize="small" /></IconButton>
                                        </Stack>
                                    ) : (
                                        <Button size="small" variant="text" onClick={() => startEdit('distance', offer.distance)}
                                                sx={{ justifyContent: 'flex-start', color: WALNUT, textTransform: 'none' }}>
                                            Distance: {offer.distance ? `${offer.distance} km` : '—'}
                                        </Button>
                                    )}
                                    {/* Duration */}
                                    {activeEdit === 'duration' ? (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <TextField size="small" type="number" label="Duration (days)" value={tempValue}
                                                       onChange={e => setTempValue(e.target.value)} sx={{ flex: 1 }} />
                                            <IconButton size="small" color="success" onClick={() => saveField('duration', tempValue ? Number(tempValue) : null)}><SaveIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => setActiveEdit(null)}><CloseIcon fontSize="small" /></IconButton>
                                        </Stack>
                                    ) : (
                                        <Button size="small" variant="text" onClick={() => startEdit('duration', offer.duration ?? '')}
                                                sx={{ justifyContent: 'flex-start', color: WALNUT, textTransform: 'none' }}>
                                            Duration: {offer.duration ? `${offer.duration} days` : '—'}
                                        </Button>
                                    )}
                                    {/* Base price */}
                                    {activeEdit === 'basePrice' ? (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <TextField size="small" type="number" label="Base price (€)" value={tempValue}
                                                       onChange={e => setTempValue(e.target.value)} sx={{ flex: 1 }}
                                                       inputProps={{ step: '0.01', placeholder: 'e.g. 50 for €50.00' }} />
                                            <IconButton size="small" color="success" onClick={() => saveField('basePrice', Math.round(parseFloat(tempValue || 0) * 100))}><SaveIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => setActiveEdit(null)}><CloseIcon fontSize="small" /></IconButton>
                                        </Stack>
                                    ) : (
                                        <Button size="small" variant="text" onClick={() => startEdit('basePrice', ((offer.basePrice || 0) / 100).toFixed(2))}
                                                sx={{ justifyContent: 'flex-start', color: WALNUT, textTransform: 'none' }}>
                                            Base price: {formatMoney(offer.basePrice || 0, offer.currency || 'EUR')}
                                        </Button>
                                    )}
                                </Stack>
                            </Paper>
                        )}

                        {/* Booking card */}
                        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden',
                            border: `1px solid rgba(201,151,58,0.25)`,
                            boxShadow: '0 8px 32px rgba(26,26,46,0.1)' }}>

                            {/* Card header */}
                            <Box sx={{ px: 3, py: 2.5,
                                background: `linear-gradient(135deg, ${MIDNIGHT} 0%, #2A2A4E 100%)` }}>
                                <Stack direction="row" alignItems="baseline" spacing={0.8}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                                        From
                                    </Typography>
                                    <Typography variant="h5" fontWeight={900} sx={{ color: GOLD }}>
                                        {formatMoney(offer.basePrice || 0, offer.currency || 'EUR')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>/ person</Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" gap={0.8}>
                                    {offer.difficulty && (
                                        <Chip label={offer.difficulty} size="small"
                                              sx={{ bgcolor: diffStyle.bg, color: diffStyle.color, fontWeight: 800, fontSize: '0.7rem' }} />
                                    )}
                                    {offer.distance && (
                                        <Chip icon={<StraightenIcon sx={{ fontSize: '0.8rem !important', color: 'rgba(255,255,255,0.7) !important' }} />}
                                              label={`${offer.distance} km`} size="small"
                                              sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.85)', fontWeight: 700 }} />
                                    )}
                                    {offer.region && (
                                        <Chip icon={<LocationOnIcon sx={{ fontSize: '0.8rem !important', color: 'rgba(255,255,255,0.7) !important' }} />}
                                              label={offer.region} size="small"
                                              sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.85)', fontWeight: 700 }} />
                                    )}
                                </Stack>
                            </Box>

                            {/* Payment breakdown */}
                            <Box sx={{ bgcolor: '#fff' }}>
                                <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
                                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                                        <PaymentsIcon sx={{ color: GOLD, fontSize: '1.2rem' }} />
                                        <Typography variant="caption" fontWeight={900}
                                                    sx={{ color: WALNUT, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                            Payment Schedule
                                        </Typography>
                                    </Stack>

                                    {/* 30% deposit */}
                                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(201,151,58,0.08)',
                                        border: `1px solid rgba(201,151,58,0.25)`, mb: 1.5 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                            <Box>
                                                <Typography variant="body2" fontWeight={800} sx={{ color: WALNUT }}>
                                                    30% — at booking
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    Secures your spot
                                                </Typography>
                                            </Box>
                                            <Typography variant="h6" fontWeight={900} sx={{ color: GOLD }}>
                                                {formatMoney(deposit30, offer.currency || 'EUR')}
                                            </Typography>
                                        </Stack>
                                    </Box>

                                    {/* 70% balance */}
                                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(74,103,65,0.06)',
                                        border: `1px solid rgba(74,103,65,0.2)` }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                            <Box>
                                                <Typography variant="body2" fontWeight={800} sx={{ color: WALNUT }}>
                                                    70% — 15 days before
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    Remaining balance
                                                </Typography>
                                            </Box>
                                            <Typography variant="h6" fontWeight={900} sx={{ color: FOREST }}>
                                                {formatMoney(remaining70, offer.currency || 'EUR')}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                </Box>

                                <Divider />

                                {/* Book button */}
                                <Box sx={{ px: 3, py: 2.5 }}>
                                    {!user && (
                                        <Alert severity="info" icon={<PersonIcon />}
                                               sx={{ mb: 2, borderRadius: 2, fontSize: '0.8rem', py: 0.5 }}>
                                            An account is required to book this journey.
                                        </Alert>
                                    )}
                                    <Button fullWidth variant="contained" size="large"
                                            onClick={handleOpenBooking}
                                            sx={{ py: 1.6, fontWeight: 900, fontSize: '1rem',
                                                background: `linear-gradient(135deg, ${MIDNIGHT} 0%, #2A2A4E 100%)`,
                                                color: GOLD, borderRadius: 2.5,
                                                border: `1px solid rgba(201,151,58,0.35)`,
                                                boxShadow: '0 4px 20px rgba(26,26,46,0.25)',
                                                letterSpacing: '0.04em',
                                                '&:hover': { background: `linear-gradient(135deg, #2A2A4E 0%, #3A3A6E 100%)`,
                                                    boxShadow: '0 6px 28px rgba(26,26,46,0.35)', transform: 'translateY(-1px)' },
                                                transition: 'all 0.2s' }}>
                                        <DirectionsBikeIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                                        {user ? 'Book This Journey' : 'Log in to Book'}
                                    </Button>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                </Box>
            </Container>

            {/* ── Login gate modal ─────────────────────────────────────────── */}
            <Dialog open={loginOpen} onClose={() => setLoginOpen(false)}
                    maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                {user ? (
                    <>
                        <DialogTitle fontWeight={800} sx={{ color: MIDNIGHT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                            {bookingStep === 1 ? 'Book This Journey' : 'Confirm & Pay'}
                        </DialogTitle>
                        <DialogContent>
                            {bookingStep === 1 ? (
                                <>
                                    <Typography variant="body2" sx={{ color: '#4A3728', mb: 2 }}>
                                        Choose your start date and number of participants for <strong>{offer.title}</strong>.
                                    </Typography>

                                    {/* Date picker */}
                                    <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                                        Start date (minimum 15 days from today)
                                    </Typography>
                                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                                        <MiniCalendar
                                            value={bookingDate}
                                            onChange={date => { setBookingDate(date); setBookingError(''); }}
                                            minDate={minDateObj}
                                        />
                                    </Box>

                                    {/* Participant count */}
                                    <TextField
                                        fullWidth type="number" size="small" label="Participants"
                                        inputProps={{ min: 1 }}
                                        value={bookingPax}
                                        onChange={e => setBookingPax(Math.max(1, parseInt(e.target.value) || 1))}
                                        sx={{ mb: 2 }} />

                                    {/* Price preview */}
                                    {bookingDate && bookingPax >= 1 && (
                                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(201,151,58,0.06)',
                                            border: `1px solid rgba(201,151,58,0.2)`, mb: 2 }}>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                Price breakdown
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: WALNUT }}>
                                                Total: <strong>{formatMoney(totalCents, offer.currency || 'EUR')}</strong>
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: WALNUT }}>
                                                30% deposit: <strong>{formatMoney(depositCents, offer.currency || 'EUR')}</strong>
                                            </Typography>
                                        </Box>
                                    )}

                                    {bookingError && (
                                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontSize: '0.8rem' }}>
                                            {bookingError}
                                        </Alert>
                                    )}

                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                        Minimum 15 days notice required. Stripe test card: 4242 4242 4242 4242.
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography variant="body2" sx={{ color: '#4A3728', mb: 2 }}>
                                        You are booking <strong>{offer.title}</strong> for <strong>{bookingPax} participant{bookingPax !== 1 ? 's' : ''}</strong> starting <strong>{bookingDate?.toLocaleDateString('en-CA')}</strong>.
                                    </Typography>

                                    {bookingMode === 'BOOK_NOW' ? (
                                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(74,103,65,0.06)',
                                            border: `1px solid rgba(74,103,65,0.2)`, mb: 1.5 }}>
                                            <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT }}>Full payment</Typography>
                                            <Typography variant="h6" fontWeight={900} sx={{ color: FOREST }}>
                                                {formatMoney(totalCents, offer.currency || 'EUR')}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <>
                                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(201,151,58,0.08)',
                                                border: `1px solid rgba(201,151,58,0.25)`, mb: 1.5 }}>
                                                <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT }}>30% deposit — charged now</Typography>
                                                <Typography variant="h6" fontWeight={900} sx={{ color: GOLD }}>
                                                    {formatMoney(depositCents, offer.currency || 'EUR')}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(74,103,65,0.06)',
                                                border: `1px solid rgba(74,103,65,0.2)`, mb: 1.5 }}>
                                                <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT }}>
                                                    70% balance — due 15+ days before start date
                                                </Typography>
                                                <Typography variant="h6" fontWeight={900} sx={{ color: FOREST }}>
                                                    {formatMoney(totalCents - depositCents, offer.currency || 'EUR')}
                                                </Typography>
                                            </Box>
                                        </>
                                    )}

                                    <Alert severity="info" sx={{ borderRadius: 2, fontSize: '0.8rem' }}>
                                        You will be redirected to Stripe's secure payment page.
                                    </Alert>

                                    {bookingError && (
                                        <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2, fontSize: '0.8rem' }}>
                                            {bookingError}
                                        </Alert>
                                    )}
                                </>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexWrap: 'wrap' }}>
                            {bookingStep === 1 ? (
                                <>
                                    <Button onClick={() => setLoginOpen(false)}>Cancel</Button>
                                    <Box sx={{ flexGrow: 1 }} />
                                    <Button variant="outlined" onClick={() => handleSelectMode('RESERVE')}
                                            sx={{ borderColor: GOLD, color: GOLD, fontWeight: 700, borderRadius: 2 }}>
                                        Reserve — 30% now
                                    </Button>
                                    <Button variant="contained" onClick={() => handleSelectMode('BOOK_NOW')}
                                            sx={{ background: `linear-gradient(135deg, ${MIDNIGHT} 0%, #2A2A4E 100%)`,
                                                color: GOLD, borderRadius: 2, fontWeight: 700 }}>
                                        Book Now — full price
                                    </Button>
                                </>
                            ) : bookingLoading ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', py: 1.5, gap: 1.5 }}>
                                    <CircularProgress size={32} sx={{ color: MIDNIGHT }} />
                                    <Typography variant="body2" fontWeight={600} sx={{ color: WALNUT }}>
                                        {bookingMode === 'BOOK_NOW' ? 'Creating your booking…' : 'Preparing payment…'}
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <Button onClick={() => setBookingStep(1)}>Back</Button>
                                    <Box sx={{ flexGrow: 1 }} />
                                    <Button variant="contained" onClick={handleConfirmBooking}
                                            sx={{ background: `linear-gradient(135deg, ${MIDNIGHT} 0%, #2A2A4E 100%)`,
                                                color: GOLD, borderRadius: 2, fontWeight: 700 }}>
                                        Proceed to Payment
                                    </Button>
                                </>
                            )}
                        </DialogActions>
                    </>
                ) : (
                    <>
                        <DialogTitle fontWeight={800} sx={{ color: MIDNIGHT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                            Account Required
                        </DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" sx={{ color: '#4A3728', mb: 2 }}>
                                You need an account to book <strong>{offer.title}</strong>.
                                Sign in or create an account to continue.
                            </Typography>
                            <Alert severity="info" sx={{ borderRadius: 2, fontSize: '0.8rem' }}>
                                After logging in you'll be brought back to this page.
                            </Alert>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexDirection: 'column', alignItems: 'stretch' }}>
                            <Button fullWidth variant="contained"
                                    component="a"
                                    href={`/login?redirect=/bike-travel-offers/${id}`}
                                    sx={{ background: `linear-gradient(135deg, ${MIDNIGHT} 0%, #2A2A4E 100%)`,
                                        color: GOLD, borderRadius: 2, fontWeight: 700, py: 1.3 }}>
                                Log In
                            </Button>
                            <Button fullWidth variant="outlined"
                                    component="a"
                                    href={`/register?redirect=/bike-travel-offers/${id}`}
                                    sx={{ borderColor: MIDNIGHT, color: MIDNIGHT, borderRadius: 2, fontWeight: 700, py: 1.3 }}>
                                Create Account
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* ── Delete dialog ────────────────────────────────────────────── */}
            <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)}
                    maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={700} sx={{ color: '#b71c1c' }}>Delete Offer</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 0.5 }}>
                        <Typography variant="body2">
                            You are about to delete <strong>{offer?.title}</strong>. This cannot be undone.
                            Enter your password to confirm.
                        </Typography>
                        <TextField fullWidth size="small" type="password" label="Your password"
                                   value={deletePassword}
                                   onChange={e => { setDeletePassword(e.target.value); setDeleteError(''); }}
                                   onKeyDown={e => e.key === 'Enter' && !deleteLoading && handleDelete()}
                                   error={!!deleteError} helperText={deleteError} autoFocus />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete}
                            disabled={deleteLoading || !deletePassword} sx={{ borderRadius: 2, fontWeight: 700 }}>
                        {deleteLoading ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
